import moment from "moment-timezone";
import GroupRegistrationModel, {
  isGroupRegistration,
} from "src/models/GroupRegistrationModel";
import MembershipModel, { isMembership } from "src/models/MembershipModel";
import PrivateSerieModel, {
  isPrivateSerie,
} from "src/models/PrivateSerieModel";
import { useStore } from "src/stores/Store";
import { dateFormat, dateFormatMed } from "src/utils/constants";
import tw from "src/utils/tw";
import {
  CustomerRequestStatus,
  CustomerRequestType,
  EventType,
  GroupRegistrationStatus,
  MembershipStatus,
  PrivateSerieStatus,
} from "types/code-generator";

interface Props {
  registration: GroupRegistrationModel | PrivateSerieModel | MembershipModel;
  className?: string;
  showDate?: boolean;
  hidePendingCancel?: boolean;
  isPending?: boolean;
  gradient?: boolean;
}

export default function EnrollmentStatus(props: Props) {
  const store = useStore();
  const { registration } = props;

  let label = registration.status?.toString();
  let isPendingCancel = false;
  let labelColor = "text-dark";
  let icon;
  let iconColor;
  let date;
  let borderColor = "border-lightGray";

  if (isGroupRegistration(registration)) {
    if (props.isPending) {
      if (registration.customerRequest?.type === CustomerRequestType.Cancel) {
        return null;
      }

      if (registration.customerRequest?.type === CustomerRequestType.Move) {
        return null;
      }

      if (registration.finalDate) {
        const today = moment();
        const finalDate = moment(registration.finalDate);

        if (finalDate.isAfter(today)) {
          return null;
        }
      }
    }

    if (registration.status === GroupRegistrationStatus.Pending) {
      icon = "fa-triangle-exclamation text-main";
      borderColor = "border-main";
      label = "Pending approval";

      if (props.showDate) {
        date = `since ${moment(
          registration.dateApproved || registration.createdAt
        ).format(dateFormat)}`;
      }
    } else if (registration.status === GroupRegistrationStatus.Approved) {
      icon = "fa-bolt-lightning text-success";
      label = "Enrolled";
      borderColor = "border-success";

      if (props.showDate) {
        date = `since ${moment(registration.dateApproved).format(dateFormat)}`;
      }

      if (registration.finalDate) {
        const today = moment();
        const finalDate = moment(registration.finalDate);

        if (finalDate.isAfter(today)) {
          icon = "fa-bolt-lightning text-error";
          label = "Pending cancellation";
          date = `${moment(registration.finalDate).format(dateFormatMed)}`;
          borderColor = "border-error";
        }
      }

      if (
        registration.customerRequest &&
        registration.customerRequest.finalDate &&
        registration.customerRequest.status === CustomerRequestStatus.Pending &&
        !props.isPending
      ) {
        const today = moment();
        const finalDate = moment(registration.customerRequest.finalDate);

        if (finalDate.isAfter(today)) {
          icon = "fa-bolt-lightning text-error";
          label = "Cancellation requested";
          date = `Final date ${moment(
            registration.customerRequest.finalDate
          ).format(dateFormatMed)}`;
        }
      }
    } else if (registration.status === GroupRegistrationStatus.Waitlist) {
      icon = "fa-triangle-exclamation text-organization";
      iconColor = store.theme.orgColor;
      borderColor = "border-organization";
    } else if (registration.status === GroupRegistrationStatus.Moved) {
      icon = "fa-arrow-right-from-line text-empty";
      labelColor = "text-empty";

      if (props.showDate) {
        const event = registration.events.find(
          (e) => e.type === EventType.Moved
        );
        date = event && `on ${moment(event.createdAt).format(dateFormat)}`;
      }
    } else if (registration.status === GroupRegistrationStatus.Completed) {
      icon = "fa-door-closed text-empty";
      labelColor = "text-empty";
      label = "Completed";

      if (props.showDate) {
        const event = registration.events.find(
          (e) => e.type === EventType.Completed
        );
        date = event && `on ${moment(event.createdAt).format(dateFormat)}`;
      }
    } else if (registration.status === GroupRegistrationStatus.Canceled) {
      icon = "fa-ban text-empty";
      labelColor = "text-empty";

      if (props.showDate) {
        let event = registration.events.find(
          (e) => e.type === EventType.Canceled
        );

        if (!event) {
          event = registration.events.find(
            (e) => e.type === EventType.CancelApproved
          );
        }

        date = event && `on ${moment(event.createdAt).format(dateFormat)}`;
      }
    } else if (registration.status === GroupRegistrationStatus.Declined) {
      icon = "fa-ban text-empty";
      labelColor = "text-empty";
    }
  }

  if (isPrivateSerie(registration)) {
    if (registration.status === PrivateSerieStatus.Pending) {
      icon = "fa-triangle-exclamation text-main";
      label = "Pending Assignment";
      borderColor = "border-main";

      if (props.isPending) {
        icon = "fa-arrow-right-to-arc text-main";
        label = `Pending ${store.organization.terminology.staff.toLowerCase()} assignment`;
      }

      if (props.showDate) {
        date = `since ${moment(props.registration.createdAt).format(
          dateFormat
        )}`;
      }
    } else if (registration.status === PrivateSerieStatus.Posted) {
      icon = "fa-alarm-clock text-success";
      label = "Posted for Claim";

      if (store.isAccountArea) {
        icon = "fa-triangle-exclamation text-main";
        label = "Pending Assignment";
      }
    } else if (registration.status === PrivateSerieStatus.InProgress) {
      icon = "fa-bolt-lightning text-success";
      label = "In Progress";

      if (props.showDate) {
        const updated = moment.utc(props.registration.updatedAt);
        const today = moment().utc().startOf("day");
        const idleTime = today.diff(updated, "days").toLocaleString();
        date = `Idle ${idleTime} day${idleTime !== "1" && "s"}`;
      }
    } else if (registration.status === PrivateSerieStatus.Abandoned) {
      icon = "fa-pause text-error";
      label = "Paused";
    } else if (
      registration.status === PrivateSerieStatus.Completed ||
      registration.status === PrivateSerieStatus.Archived ||
      registration.status === PrivateSerieStatus.Canceled
    ) {
      icon = "fa-power-off text-empty";
      labelColor = "text-empty";
      label = "Inactive";
    }
  }

  if (isMembership(registration)) {
    if (props.isPending) {
      if (registration.customerRequest?.type === CustomerRequestType.Cancel) {
        return null;
      }

      if (registration.customerRequest?.type === CustomerRequestType.Move) {
        return null;
      }

      if (registration.finalDate) {
        const today = moment();
        const finalDate = moment(registration.finalDate);

        if (finalDate.isAfter(today)) {
          return null;
        }
      }
    }

    if (registration.status === MembershipStatus.Active) {
      icon = "fa-bolt-lightning text-success";
      label = "Enrolled";

      if (props.showDate) {
        date = `since ${moment(
          registration.dateApproved || registration.createdAt
        ).format(dateFormat)}`;
      }

      if (
        registration.customerRequest &&
        registration.customerRequest.type === CustomerRequestType.Cancel &&
        registration.customerRequest.status === CustomerRequestStatus.Pending &&
        !registration.finalDate
      ) {
        isPendingCancel = true;
      }
    } else if (registration.status === MembershipStatus.Pending) {
      icon = "fa-triangle-exclamation text-main";
      label = "Pending";
    } else if (registration.status === MembershipStatus.Closed) {
      icon = "fa-door-closed text-empty";
      labelColor = "text-empty";
      label = "Closed";
    } else if (registration.status === MembershipStatus.Waitlist) {
      icon = "fa-triangle-exclamation";
      iconColor = store.theme.orgColor;
    } else if (registration.status === MembershipStatus.Paused) {
      icon = "fa-pause text-main";
      label = "Paused";
    }

    if (registration.finalDate) {
      const today = moment();
      const finalDate = moment(registration.finalDate);

      if (today.isBefore(finalDate)) {
        icon = "fa-bolt-lightning text-error";
        label = "Pending cancellation";
        date = finalDate.format(dateFormat);
      }
    }
  }

  if (props.hidePendingCancel && isPendingCancel) {
    return null;
  }

  if (props.gradient) {
    return (
      <div
        className={tw(
          "mb-3 max-w-sm border-l-4 bg-gradient-to-r from-lightGray via-white to-white px-4 py-2 text-sm",
          borderColor
        )}
      >
        <div className="flex items-center gap-1">
          <div className="w-5 text-center">
            <i aria-hidden className={tw("far mt-1 w-3", icon)} />
          </div>
          <div className="flex-1 text-sm">
            <strong>{label}</strong>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={tw("flex gap-1 text-sm", props.className)}>
      {icon && (
        <div className="w-5 text-center">
          <i
            className={tw("far mt-1 w-3", icon)}
            css={{
              color: iconColor ? iconColor : undefined,
            }}
          />
        </div>
      )}
      <div className="flex-1">
        <strong className={labelColor}>
          {label}{" "}
          {date && (
            <>
              <span className="font-normal"> • {date}</span>
            </>
          )}
        </strong>
      </div>
    </div>
  );
}
